/** Row shapes returned by the [Research] stored procedures. */

export interface ResearcherProfile {
  ResearcherID: number;
  FullName: string;
  Email: string;
  Contact: string;
  Designation: string;
  Institution: string;
  IsSrcAdmin: boolean;
  IsIrbAdmin: boolean;
  IsReviewer: boolean;
  IsAuthenticated: boolean;
}

export interface ResearchTypeRow {
  ResTypeId: number;
  Description: string;
}

export interface DocumentSlotRow {
  DocCode: number;
  DocName: string;
  FileKey: string;
  OrdId: number;
  IsRequired: boolean;
}

export interface StatusRow {
  StatusId: string;
  Description: string;
  ResearcherDescription: string;
}

export interface PortalConfig {
  turnstileSiteKey: string;
  maxDocumentMB: number;
  researchTypes: ResearchTypeRow[];
  documentSlots: DocumentSlotRow[];
  statuses: StatusRow[];
  maxDurationMonths: number;
  irbEmail: string;
}

export interface ProposalSummaryRow {
  ResearchCode: number;
  IRBno: number;
  IrbNumber: string | null;
  StatusId: string;
  StatusDescription: string;
  StatusForResearcher: string;
  IsSubmitted: boolean;
  SubmitDateTime: string | null;
  Revisions: number;
  Title: string | null;
  ProposalTitle: string | null;
  ReviewCategory: string | null;
  /** A merged submission PDF exists for this proposal. */
  HasDocument?: boolean;
}

export interface ProposalHeaderRow {
  ResearchCode: number;
  IRBno: number;
  IrbNumber: string | null;
  StatusId: string;
  StatusDescription: string;
  StatusForResearcher: string;
  IsSubmitted: boolean;
  SubmitDateTime: string | null;
  Revisions: number;
  FilePath: string;
  FileName: string;
}

export interface ProposalBodyRow {
  Title: string;
  ReviewCategory: string | null;
  StartDate: string;
  DurationMonths: string;
  Introduction: string;
  Rationale: string;
  Objectives: string;
  StudyType: string;
  SampleSize: string;
  SamplingTech: string;
  StudyPopulation: string;
  Inclusion: string;
  ExclCriteria: string;
  StudyInstrument: string;
  ProcedureDet: string;
  DataAnalysis: string;
  ReferencesDet: string;
  InstitutionalFacilities: string;
  Ethical: string;
  Dissemination: string;
  PotentialBenefits: string;
  PotentialRisks: string;
  HasOtherType: boolean;
  OtherTypeReason: string;
}

export interface GanttRow {
  ActivityID: number;
  Activity: string;
  [month: string]: number | string | boolean;
}

export interface SubmissionRow {
  GrantRequired: boolean;
  GrantAmount: number;
  Title: string;
  PiName: string;
  PiTitle: string;
  PiInstitution: string;
  PiContact: string;
  PiEmail: string;
  HodName: string;
  SupervisorName: string;
  RacNo: string;
  SubmissionDate: string;
}

export interface CoInvestigatorRow {
  CoInvestigatorID: number;
  Name: string;
  Title: string;
  Department: string;
  Contact: string;
}

export interface DocumentRow {
  DocCode: number;
  DocName: string;
  FileKey: string;
  OrdId: number;
  IsUploaded: boolean;
  FileName: string;
  FilePath: string;
  LastUploadedOn: string | null;
  IsRequired?: boolean;
}

/** Whether a sent-back proposal still owes the committee a cover letter. */
export interface CoverLetterRow {
  IsRequired: boolean;
  StatusId: string | null;
  IsUploaded: boolean;
  OriginalFileName: string | null;
  UploadedAt: string | null;
  HasObservationLetter: boolean;
}

export interface HistoryRow {
  StatusCode: string;
  StatusDescription: string;
  StatusForResearcher: string;
  ActionedBy: string;
  ActionedOn: string;
  Remarks: string;
  FilePath?: string;
  FileName?: string;
}

export interface ReadinessIssue {
  StepId: string;
  Issue: string;
}

/* ---------------------------------------------------------------- committee */

export interface StatusCountRow {
  StatusId: string;
  Description: string;
  Total: number;
}

export interface CommitteeInboxRow {
  SrcPending: number;
  IrbPending: number;
  ReviewsPending: number;
}

export interface CommitteeProposalRow {
  ResearchCode: number;
  IRBno: number;
  IrbNumber: string | null;
  StatusId: string;
  StatusDescription: string;
  IsSubmitted: boolean;
  SubmitDateTime: string | null;
  Revisions: number;
  ResearcherID: number;
  Title: string;
  PiName: string;
  Institution: string;
  ResearcherName: string;
  ResearcherEmail: string;
  ReviewCategory: string | null;
  ReviewersAssigned: number;
  ReviewsCompleted: number;
}

export interface WorkBucketRow {
  BucketKey: string;
  StatusId: string;
  Label: string;
  Hint: string;
  ActionLabel: string;
  SortOrder: number;
  Total: number;
  Waiting14Plus: number;
}

export interface WorkItemRow {
  BucketKey: string;
  ResearchCode: number;
  IRBno: number;
  IrbNumber: string | null;
  StatusId: string;
  StatusDescription: string;
  Title: string;
  PiName: string;
  ResearcherName: string;
  ResearcherEmail: string;
  ReviewCategory: string | null;
  SubmitDateTime: string | null;
  Revisions: number;
  DaysWaiting: number;
  ReviewersAssigned: number;
  ReviewsCompleted: number;
  ReviewsOverdue: number;
}

export interface ActivityRow {
  ResearchCode: number;
  IrbNumber: string | null;
  Title: string;
  StatusCode: string;
  StatusDescription: string;
  ActionedBy: string;
  ActionedOn: string;
}

export interface ReviewerWorkloadRow {
  Pending: number;
  Overdue: number;
  DueThisWeek: number;
  Completed: number;
}

export interface ReviewerWorkItemRow extends ReviewQueueRow {
  DueOn: string | null;
  /** Negative when the due date has passed; null when no date was set. */
  DaysLeft: number | null;
}

export interface ReviewerRow {
  ReviewerId: number;
  Name: string;
  Email: string;
  IsAssigned: boolean;
  OpenReviews: number;
}

export interface AssignmentRow {
  ReviewerId: number;
  ReviewerName: string;
  ReviewerEmail: string;
  IsReviewed: boolean;
  ReviewedOn: string | null;
  ReviewedBy: string;
  StartDate: string;
  DueDate: string;
}

/* ----------------------------------------------------------------- reviewer */

export interface ReviewQueueRow {
  ResearchCode: number;
  IRBno: number;
  IrbNumber: string | null;
  Title: string;
  PiName: string;
  ResearcherName: string;
  ReviewCategory: string | null;
  StatusId: string;
  StatusDescription: string;
  IsReviewed: boolean;
  ReviewedOn: string | null;
  StartDate: string;
  DueDate: string;
}

export interface ReviewSectionRow {
  SectionKey: string;
  SectionName: string;
  CommentColumn: string;
  SortOrder: number;
}

export interface ReviewQuestionRow {
  QuestionID: number;
  SectionKey: string;
  SectionName: string;
  ColumnName: string;
  QuestionText: string;
  SortOrder: number;
  /** 1 Yes · 0 No · 2 N/A · 3 not yet answered */
  Answer: number;
}

export interface ReviewCommentRow {
  SectionKey: string;
  Comment: string | null;
}

export interface ReviewSummaryAnswerRow {
  ReviewerId: number;
  QuestionID: number;
  SectionKey: string;
  SectionName: string;
  QuestionText: string;
  SortOrder: number;
  Answer: number;
  AnswerText: string;
}

export interface ReviewSummaryCommentRow {
  ReviewerId: number;
  SectionKey: string;
  SectionName: string;
  Comment: string;
}

export interface ReviewSummaryReviewerRow {
  ReviewerId: number;
  ReviewerName: string;
  IsReviewed: boolean;
  ReviewedOn: string | null;
  DueDate: string;
  Recommendation: string;
}

export const ANSWER_YES = 1;
export const ANSWER_NO = 0;
export const ANSWER_NA = 2;
export const ANSWER_BLANK = 3;
